import { useCallback, useEffect } from 'react';
import { ProductItemModel } from '../../data-layer/posts/types';
import { loadProductsCollectionAsync } from '../../data-layer/products/actions';
import {
    selectProductsCollection,
    selectProductsCollectionInitialized,
    selectProductsCollectionLoading,
} from '../../data-layer/products/selectors';
import { useDispatch } from '../../data-layer/useDispatch';
import { useSelector } from '../../data-layer/useSelector';

interface ProductsCollectionFilters {
    amount: number;
    catalogId: string;
}

interface UseProductsCollectionProps {
    namespace: string;
    filters: ProductsCollectionFilters;
}

interface ProductsCollection {
    products: ProductItemModel[];
    isLoading: boolean;
    isInitialized: boolean;
    isEndReached: boolean;
    onLoadNext: (filters: ProductsCollectionFilters) => void;
}

export const useProductsCollection = (params: UseProductsCollectionProps): ProductsCollection => {
    const dispatch = useDispatch();
    const { namespace } = params;

    const { items = [], next } = useSelector((state) => selectProductsCollection(state, namespace));
    const isLoading = useSelector((state) => selectProductsCollectionLoading(state, namespace));
    const isInitialized = useSelector((state) =>
        selectProductsCollectionInitialized(state, namespace),
    );

    const isEndReached = items.length !== 0 && !next;

    const onLoadNext = useCallback(
        (filters: ProductsCollectionFilters) =>
            dispatch(
                loadProductsCollectionAsync(namespace, {
                    ...filters,
                    next,
                }),
            ),
        [dispatch, namespace, next],
    );

    useEffect(() => {
        if (items.length === 0 && !isInitialized) {
            onLoadNext(params.filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, isInitialized]);

    return {
        products: items,
        isLoading: isLoading,
        isInitialized: isInitialized,
        isEndReached: isEndReached,
        onLoadNext,
    };
};
